import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';

import { Utils } from 'app/utils/utils';

import { DocumentCard, DocumentsData } from '../tool-content.model';

/**
 * Renders expandable document verbatim cards with sentiment badges and entity highlights.
 */
@Component({
  selector: 'app-ai-document-cards',
  imports: [TranslocoModule],
  templateUrl: './ai-document-cards.component.html',
  styleUrls: ['./ai-document-cards.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiDocumentCardsComponent {
  readonly data = input.required<DocumentsData>();

  protected readonly title = computed(() => this.data().title);
  protected readonly documents = computed(() => this.data().documents);
  protected readonly totalMatched = computed(() => this.data().totalMatched);
  protected readonly hasDocuments = computed(() => this.documents().length > 0);
  protected readonly hasMore = computed(() => this.totalMatched() > this.documents().length);

  protected readonly expandedIndices = signal<Set<number>>(new Set());

  protected isExpanded(index: number): boolean {
    return this.expandedIndices().has(index);
  }

  protected toggleExpand(index: number): void {
    this.expandedIndices.update((set) => {
      const next = new Set(set);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  protected sentimentClass(doc: DocumentCard): string {
    const s = doc.sentiment?.toLowerCase();
    if (!s) {
      return '';
    }
    switch (s) {
      case 'positive':
        return 'ai-document-cards__sentiment--positive';
      case 'negative':
        return 'ai-document-cards__sentiment--negative';
      case 'neutral':
        return 'ai-document-cards__sentiment--neutral';
      default:
        return 'ai-document-cards__sentiment--mixed';
    }
  }

  protected truncatedText(text: string): string {
    return Utils.truncateEnd(text, 150);
  }
}
